import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Button } from "@/frontend/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/frontend/components/ui/table";
import { StatusBadge } from "@/frontend/components/admin/StatusBadge";
import { Building2, BedDouble, CalendarCheck, Clock, Plus, ArrowRight, Loader2, Home } from "lucide-react";
import { useAdminResidences } from "@/backend/hooks/admin/useAdminResidences";
import { useAdminRoomTypes } from "@/backend/hooks/admin/useAdminRoomTypes";
import { useAdminBookings } from "@/backend/hooks/admin/useAdminBookings";
import { getRoomLabel } from "@/backend/lib/roomUtils";
import { format } from "date-fns";
import { useMemo } from "react";

const AdminDashboard = () => {
  const { data: residences = [], isLoading: residencesLoading } = useAdminResidences();
  const { data: roomTypes = [], isLoading: roomTypesLoading } = useAdminRoomTypes();
  const { data: bookings = [], isLoading: bookingsLoading } = useAdminBookings();

  // Guest-facing booking requests only — exclude admin-created blocks/offline bookings.
  const guestBookings = useMemo(() =>
    bookings.filter((b) => b.type !== "admin"),
    [bookings]
  );

  const pendingBookings = useMemo(() =>
    guestBookings.filter((b) => b.status === "pending"),
    [guestBookings]
  );

  // Recent activity excludes pure availability blocks (no guest to show).
  const recentBookings = useMemo(() =>
    bookings
      .filter((b) => b.status !== "blocked")
      .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
      .slice(0, 5),
    [bookings]
  );

  const isLoading = residencesLoading || roomTypesLoading || bookingsLoading;

  const stats = [
    {
      title: "Total Residences",
      value: residences.length,
      icon: Building2,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      title: "Total Rooms",
      value: roomTypes.length,
      icon: BedDouble,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      title: "Pending Bookings",
      value: pendingBookings.length,
      icon: Clock,
      color: "text-amber-600",
      bgColor: "bg-amber-100",
      highlight: true,
    },
    {
      title: "Total Bookings",
      value: guestBookings.length,
      icon: CalendarCheck,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-muted-foreground">
          Welcome back! Here's what's happening.
        </h2>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.title} className={stat.highlight ? "ring-2 ring-amber-300" : ""}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-full ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button asChild>
          <Link to="/admin/residences">
            <Plus className="h-4 w-4 mr-2" />
            Add Residence
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/admin/bookings">
            View All Bookings
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </Button>
      </div>

      {/* Recent Bookings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Residence</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Check-in</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentBookings.map((booking) => {
                  const residence = residences.find(r => r.id === booking.residenceId);
                  const room = getRoomLabel(booking.roomTypeId, roomTypes);
                  return (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.guests?.[0]?.name}</p>
                          <p className="text-sm text-muted-foreground">{booking.guests?.[0]?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{residence?.name || "Unknown"}</TableCell>
                      <TableCell>
                        <div>
                          <p>{room.name}</p>
                          {room.typologyKey && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Home className="h-3 w-3" />
                              {room.typologyKey}
                              {room.unitNumber && ` · Unit ${room.unitNumber}`}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{format(booking.checkIn.toDate(), "MMM d, yyyy")}</TableCell>
                      <TableCell>
                        <StatusBadge status={booking.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(booking.createdAt.toDate(), "MMM d, yyyy")}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3">
            {recentBookings.map((booking) => {
              const residence = residences.find(r => r.id === booking.residenceId);
              const room = getRoomLabel(booking.roomTypeId, roomTypes);
              return (
                <div key={booking.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{booking.guests?.[0]?.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{booking.guests?.[0]?.email}</p>
                    </div>
                    <StatusBadge status={booking.status} className="flex-shrink-0" />
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>{residence?.name || "Unknown"}</p>
                    <p>
                      Room: {room.name}
                      {room.typologyKey && (
                        <span className="ml-1 inline-flex items-center gap-1">
                          <Home className="h-3 w-3" />
                          {room.typologyKey}
                          {room.unitNumber && ` · Unit ${room.unitNumber}`}
                        </span>
                      )}
                    </p>
                    <p>Check-in: {format(booking.checkIn.toDate(), "MMM d, yyyy")}</p>
                    <p>Created: {format(booking.createdAt.toDate(), "MMM d, yyyy")}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
